import { createApp } from 'vue';
import { DateHelpers } from 'ihunter-common';

// TODO: support both light and dark palettes (currently only gets variables for the default/light palette)
const CSS_CLASS_NAMES = ['default-palette', 'pin'];

let computedStyle = null;
const cssPropertyValues = {};

const useHtmlHelpers = () => {
    const createHtmlElementFromComponent = (component, properties) => {
        const app = createApp(component, properties);
        const element = document.createElement('div');
        app.mount(element);
        return element;
    };

    const createHtmlElementFromXmlString = (xmlString) => {
        if (typeof window.DOMParser !== 'undefined') {
            return new window.DOMParser().parseFromString(xmlString, 'text/xml');
        } else if (typeof window.ActiveXObject != 'undefined' && new window.ActiveXObject('Microsoft.XMLDOM')) {
            var xmlDoc = new window.ActiveXObject('Microsoft.XMLDOM');
            xmlDoc.async = 'false';
            xmlDoc.loadXML(xmlString);
            return xmlDoc;
        } else {
            throw new Error('No XML parser found');
        }
    };

    const rasterizeSvgElementAsImage = (svgElement, width, height) => {
        const image = new Image();
        if (width && height) {
            image.width = width;
            image.height = height;
        }
        const svgXml = new XMLSerializer().serializeToString(svgElement.querySelector('svg'));
        const encodedSvg = encodeURIComponent(svgXml);
        image.src = `data:image/svg+xml;utf8,${encodedSvg}`;
        // Alternative: base64 encode. This increases size and decreases readability, so better to stick with utf8 unless base64 encoding is necessary.
        // image.src = `data:image/svg+xml;base64,${btoa(svgXml)}`;
        return image;
    };

    const createImageElementFromComponent = (component, properties) => {
        try {
            const svgElement = createHtmlElementFromComponent(component, properties);
            return rasterizeSvgElementAsImage(svgElement);
        } catch (error) {
            console.error(`createImageElementFromComponent`, error);
        }
    };

    /* 
    Extracts XML nodes from an XML string, where nodesToExtract indicates the name and type of the nodes to be extracted.
    
    Supported types:
    - String (default)
    - Number (integer or float)
    - Boolean
    - Date (must be in a format supported by DateHelpers.ConvertDateToEpochTimeMs)
    - Array (comma-delimited list of items)

    e.g. extractXmlNodesFromString(annotationXML, 'annotation', [{ name: 'color' }, { name: 'thickness', type: Number }])

    Returns an array of items that have the specified nodes.
    e.g. [{ color: 'red', thickness: 5 }, ...]
    */
    const extractXmlNodesFromString = (xmlString, tagName, nodesToExtract) => {
        const items = [];
        const xmlElement = createHtmlElementFromXmlString(xmlString);
        const elements = xmlElement.getElementsByTagName(tagName);
        for (const key in elements) {
            if (elements[key].nodeName !== tagName) continue;

            var attributes = elements[key].attributes;
            const item = {};
            for (const attr in attributes) {
                try {
                    const nodeName = attributes[attr]?.nodeName;
                    if (!nodeName) continue;

                    const nodeToExtract = nodesToExtract.find((x) => x === nodeName || x.name === nodeName);
                    if (nodeToExtract) {
                        let nodeValue = attributes[attr].nodeValue;
                        if (nodeToExtract.type === Number) {
                            nodeValue = parseFloat(nodeValue);
                        } else if (nodeToExtract.type === Boolean) {
                            nodeValue = Boolean(nodeValue);
                        } else if (nodeToExtract.type === Date) {
                            nodeValue = DateHelpers.ConvertDateToEpochTimeMs(nodeValue);
                        } else if (nodeToExtract.type === Array) {
                            nodeValue = nodeValue.split(',');
                        }
                        item[nodeName] = nodeValue;
                    }
                } catch (error) {
                    console.error(`extractXmlNodesFromString failed to process attribute ${attr} of element ${key}:`, error);
                }
            }
            items.push(item);
        }
        return items;
    };

    /**
     * Generates an XML string from an array of objects based on a provided constants schema.
     *
     * @param {Array<Object>} items - The collection of data objects to serialize.
     * @param {Object} constants - The constants object defining the XML structure and property schema.
     * @param {string} constants.Declaration - The XML declaration (e.g., '<?xml version="1.0"?>').
     * @param {string} constants.RootTag - The root wrapping tag.
     * @param {string} constants.ItemTag - The tag name for each individual item.
     * @param {Array<string|Object>} constants.Properties - The schema defining properties to serialize,
     *        including their types and default values.
     * @returns {string} The complete XML string.
     */
    const generateXmlStringFromNodes = (items, constants) => {
        const { Declaration, RootTag, ItemTag, Properties } = constants;

        let xmlString = `${Declaration}<${RootTag}>`;

        for (const item of items) {
            let attributesString = '';

            for (const nodeConfig of Properties) {
                try {
                    // Support both object configs and simple string configs
                    const isObject = typeof nodeConfig === 'object';
                    const nodeName = isObject ? nodeConfig.name : nodeConfig;

                    let value = item[nodeName];

                    // Fallback to default if value is missing, or empty string as last resort
                    if (value === undefined || value === null) {
                        value = isObject && nodeConfig.default !== undefined ? nodeConfig.default : '';
                    }

                    // Apply type casting if a type is specified and the value isn't an empty string fallback
                    if (isObject && value !== '') {
                        if (nodeConfig.type === Array) {
                            value = Array.isArray(value) ? value.join(',') : '';
                        } else if (nodeConfig.type === Date) {
                            value = new Date(value).getTime();
                        } else if (nodeConfig.type === Boolean || nodeConfig.type === Number) {
                            value = String(value);
                        }
                    }
                    const escapedValue = String(value).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
                    attributesString += ` ${nodeName}="${escapedValue}"`;
                } catch (error) {
                    console.error(`generateXmlStringFromNodes`, error);
                }
            }
            xmlString += `<${ItemTag}${attributesString} />`;
        }
        xmlString += `</${RootTag}>`;
        return xmlString;
    };

    const initializeComputedStyle = () => {
        if (!computedStyle) {
            const element = document.createElement('div');
            element.classList.add(...CSS_CLASS_NAMES);
            element.setAttribute('type', 'hidden');
            document.body.appendChild(element);
            computedStyle = window.getComputedStyle(element);
        }
    };

    const getCSSPropertyValue = (propertyName) => {
        if (!cssPropertyValues[propertyName]) {
            initializeComputedStyle();
            const propertyValue = computedStyle?.getPropertyValue(propertyName);
            //console.debug(`getCSSPropertyValue: ${propertyName} = ${propertyValue}`);
            if (propertyValue) {
                cssPropertyValues[propertyName] = propertyValue;
            }
        }
        return cssPropertyValues[propertyName];
    };

    return {
        createHtmlElementFromComponent,
        createHtmlElementFromXmlString,
        createImageElementFromComponent,
        rasterizeSvgElementAsImage,
        extractXmlNodesFromString,
        generateXmlStringFromNodes,
        getCSSPropertyValue,
    };
};

export { useHtmlHelpers as default, useHtmlHelpers };
